import { theme } from '@Guidelines/Theme/theme';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import type { ViewerListAction } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/ViewerListAction';
import type DocumentListEntryView from '@UI/Store/Documents/Views/DocumentListEntryView';
import { css } from '@emotion/react';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const panelStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 1rem;
`;

const titleStyle = css`
	margin: 0;
	${theme.typography.cardTitle}
	color: ${theme.colors.ink};
`;

const noteStyle = css`
	margin: 0;
	padding: 0.875rem 1rem;
	align-self: stretch;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly list: DocumentListEntryView;
	readonly isBusy: boolean;
	readonly onAction: (action: ViewerListAction) => void;
};

/** An empty list: its name, « Aucun élément » and the way to add the first element. */
const EmptyListPanel: React.FC<Props> = ({ list, isBusy, onAction }) => {
	const canEdit = useCanEditDocument();

	const handleAdd = React.useCallback(() => {
		onAction({ kind: 'add', listKey: list.key, position: 0 });
	}, [onAction, list.key]);

	return (
		<div css={panelStyle}>
			<h3 css={titleStyle}>{list.title}</h3>
			{list.items.length === 0 ? (
				<p css={noteStyle}>Aucun élément</p>
			) : (
				<p css={noteStyle}>Cette liste n'est plus vide.</p>
			)}
			<ConditionalLoader data={canEdit && list.items.length === 0}>
				<Button
					type="button"
					label="Ajouter un élément"
					icon={<Plus size={16} />}
					loading={isBusy}
					disabled={!list.canAdd}
					onClick={handleAdd}
				/>
			</ConditionalLoader>
		</div>
	);
};

export default EmptyListPanel;
